<template lang="pug">
.header-wrap(:class="[$rogue.appContext && 'mobile-app', showMenu && 'menu-open']")
  .competition-header
    rogue-link(:to="baseUrl")
      StaticImage(
        v-if="logo"
        :class="useLogoSize.className"
        :src="logo.src"
        :alt="__('Event home')"
        :autoHeight="true"
        eager
        :width="useLogoSize.width"
        :height="useLogoSize.height"
        background="#000000"
     )
    .links(:class="{'show-links': showMenu}")
      rogue-link.link(
        v-for="link in menuLinks"
        :key="link.text"
        :to="link.path"
        :class="{active: link.active}"
        data-track="competition-link"
      )
        span.live-dot(v-if="['Live', 'Watch'].includes(link.text) && live")
        | {{ __(link.text, 'auto') }}
    .fill.mobile-links(v-if="$rogue.appContext")
    .mobile-links(v-if="$rogue.appContext")
      .mobile-btn.red(@click="toggleMenu()")
        Icon.icn(:name="showMenu ? 'x' : 'hamburger'" fill="white")
  </template>
<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount, inject, type ComputedRef } from 'vue'
import { useRoute } from 'vue-router'
import { useRogue } from '../../js/rogue'
import { allowScroll, preventScroll } from '../../js/body-scroll'
import { useMQ } from '../../js/mq'

type Link = {
  text: string
  active: boolean
  path: string
}

const props = withDefaults(defineProps<{
  logo?: object
  logoSize?: 'Large Logo' | 'Med Logo' | 'Small Logo'
  baseUrl?: string
  live?: boolean
  links?: Array<{ text: string; path: string; [key: string]: any }>
}>(),{
  logoSize : 'Small Logo',
  links    : () => ([]),
})

const emit = defineEmits(['input'])

const loggedIn = inject<ComputedRef<boolean>>('loggedIn', computed(() => false))
const overlay = inject<ComputedRef<string | null>>('overlay', computed(() => null))
const showOverlay = inject<(o: string) => void>('showOverlay', (o: string) => {})

const rogue = useRogue()
const route = useRoute()
const loaded = ref(false)
const showMenu = ref(false)

const currentRoute = computed(() => {
  return route || rogue?.router?.currentRoute?.value
})

const useLogoSize = computed(() => {
  const logoSizes = {
    "Large Logo" : { className: "large-logo", height: 50, width: 150 },
    "Med Logo"   : { className: "medium-logo", height: 40, width: 110 },
    "Small Logo" : { className: "small-logo", height: 28, width: 65 },
  }
  
  return logoSizes[props.logoSize]
})

const menuLinks = computed(() => {
  return (props.links || []).filter(link => {
    const isPremiumLink = link.text.toLowerCase() === 'premium'
    if (!rogue.appContext || !isPremiumLink) return true
    
    return rogue.featureFlags?.isOn('enable-app-irongamepremium')
  })
})

const toggleMenu = () => {
  if (showMenu.value) {
    showMenu.value = false
    allowScroll()
  } else {
    preventScroll()
    showMenu.value = true
  }
  emit('input', showMenu.value)
}

const closeMenu = () => {
  if (showMenu.value) {
    toggleMenu()
  }
}

const mq = useMQ() 
  
watch(() => mq.value, () => closeMenu(), { deep: true })

watch(() => currentRoute.value.path, () => closeMenu())

onMounted(() => {
  loaded.value = true
})

onBeforeUnmount(() => {
  closeMenu()
})

</script>
<style lang="scss" scoped>
.header-wrap {
  background-color: $color-black;
  width: 100%;
  z-index: 15;

  &.menu-open {
    position: fixed;
  }

  &:not(.mobile-app) {
    @include not-desktop() {
      display: none;
    }
  }
}

@media (min-height: 470px) {
  .header-wrap {
    position: sticky;
    top: 0;
  }
}

.competition-header {
  display: flex;
  flex-wrap: nowrap;
  justify-content: center;
  align-items: center;
}

.small-logo {
  width: 65px;
  min-width: 65px;
  flex-shrink: 0;
  flex-grow: 0;
  margin-left: $space-s;
  height: 28px;
  margin-right: $space-s;
}

.medium-logo {
  width: 110px;
  min-width: 65px;
  flex-shrink: 0;
  flex-grow: 0;
  height: 40px;
}

.large-logo {
  min-width: 65px;
  flex-shrink: 0;
  flex-grow: 0;
  height: 50px;
}

.large-logo, .medium-logo  {
  width: 90px;

  @include all-desktop() {
    width: 150px;
  }

  @media (min-width: 1650px) {
    width: 185px;
  }

  @media (max-width: 1100px) {
    width: 90px;
  }
}

.mobile-links {
  display: flex;

  @include all-desktop() {
    display: none;
  }
}

.links {
  display: flex;
  gap: $space-xxs;

  @media (min-width: 1175px) {
    gap: $space-s;
  }

  @media (min-width: 1366px) and (max-width: 1649px) {
    gap: $space-m;
  }

  @media (min-width: 1650px) {
    gap: $space-xxl;
  }

  @include all-desktop() {
    .link:hover {
      color: $color-red;
    }
  }

  .link.active {
    color: $color-red;
  }

  @include not-desktop() {
    display: none;

    &.show-links {
      display: block;
      position: fixed;
      overflow-y: auto;
      top: 67px;
      bottom: 0;
      left: 0;
      z-index: 99999;
      background-color: black;
      right: 0;

      .link {
        border-top: 1px solid $color-gray-light;
      }
    }
  }
}

.live-dot {
  height: $size-xxs;
  width: $size-xxs;
  background-color: $color-red;
  margin-right: $space-xxxs;
  border-radius: 50%;
  display: inline-block;
}

.mobile-btn {
  display: flex;
  height: 72px;
  align-items: center;
  justify-content: center;
  padding: $space-xs;
  cursor: pointer;

  .icn {
    width: 29px;
    height: 29px;
  }

  &.red {
    background-color: $color-red;

    .icn {
      width: 34px;
      height: 34px;
    }
  }
}

.fill {
  flex-grow: 1;
}

.link {
  white-space: nowrap;
  font-family: $font-header;
  font-weight: $weight-bold;
  font-size: $size-s;
  text-decoration: none;
  color: $color-white;
  text-transform: uppercase;
  padding: $space-m 2px;
  width: 100%;
  display: flex;
  align-items: center;

  @include desktop() {
    font-size: $size-xs;
    line-height: $size-s;
  }

  @include not-mobile() {
    width: auto;
  }
}
</style>
